import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '@/lib/auth-guards';
import { useTRPC, type ApiError, type RouterOutputs } from '@/lib/trpc';

/**
 * How often the server's own application-access decision is re-read. It is a
 * deliberate revocation dead-man's switch, not a freshness hint: the gate that
 * admits a Staff account unmounts the router when a poll returns `allowed:
 * false`, so the interval is the revocation latency the product promises
 * (recorded in the decision that keeps the QA work queue to Staff accounts
 * holding QA Review access, ADR 0328).
 */
export const ApplicationAccessReconciliationIntervalMs = 15_000;

type TrpcOptionsProxy = ReturnType<typeof useTRPC>;

/**
 * The one place the application-access query options are built, so
 * `ApplicationAccessGate` and the navigation derivation share a cache entry, a
 * polling interval and a request (ADR 0328). `enabled` is the caller's (is this
 * a Staff account?); a blank subject disables the query here too, because the
 * input addresses one Keycloak user and there is nothing to ask about until the
 * authenticated subject is known. A disabled query is never issued, so an
 * account that does not need the decision adds no request.
 *
 * The three call options are the gate's own and are supplied here rather than at
 * each call site, because a second copy of the interval is exactly how the gate
 * and the navigation would start polling at different rates.
 */
export function applicationAccessQueryOptions(
  trpc: TrpcOptionsProxy,
  targetKeycloakUserId: string,
  enabled: boolean,
) {
  return trpc.getApplicationAccess.queryOptions(
    { targetKeycloakUserId },
    {
      enabled: enabled && targetKeycloakUserId.length > 0,
      refetchInterval: ApplicationAccessReconciliationIntervalMs,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  );
}

/**
 * The server's own application-access decision, taken from the procedure's
 * inferred output — never redeclared here. The query options (including the
 * blank-subject guard) come from {@link applicationAccessQueryOptions}.
 */
export function useApplicationAccessDecision(
  enabled: boolean,
): UseQueryResult<RouterOutputs['getApplicationAccess'], ApiError> {
  const trpc = useTRPC();
  const { user } = useAuth();

  return useQuery(applicationAccessQueryOptions(trpc, user?.id ?? '', enabled));
}
